import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Filter, FileCheck2, AlertTriangle, Waves, FileBarChart, ArrowUpRight, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { useFideicomiso } from "../../lib/fideicomiso";
import { Button } from "../../components/ui/button";

// Mapa de configuración por hub — cambia el título, tab destino y descripción
const HUB_CONFIG = {
    elegibilidad: {
        title: "Elegibilidad",
        Icon: Filter,
        tab: "elegibilidad",
        desc: "Reglas de elegibilidad por facility · versiones append-only · evaluación contra cortes de tape.",
    },
    "borrowing-base": {
        title: "Borrowing Base",
        Icon: FileCheck2,
        tab: "borrowing-base",
        desc: "Cálculo del BB neto: bruto elegible − haircuts × FX − reservas. Histórico y cushion vs monto dispuesto.",
    },
    covenants: {
        title: "Covenants",
        Icon: AlertTriangle,
        tab: "covenants",
        desc: "Medición de covenants sobre cortes de tape · semáforo · waivers · springing triggers.",
    },
    waterfall: {
        title: "Waterfall",
        Icon: Waves,
        tab: "waterfall",
        desc: "Simulador de cascada de pagos · ejecución que crea instrucciones bajo matriz de facultades.",
    },
    reportes: {
        title: "Reportes lender",
        Icon: FileBarChart,
        tab: "reportes",
        desc: "Reportes mensuales PDF + Excel · firma mock · envío al lender · portal externo espejo.",
    },
};

export default function FacilityHub({ hub }) {
    const cfg = HUB_CONFIG[hub];
    const { selected } = useFideicomiso();
    const [facilities, setFacilities] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!selected) return;
        setLoading(true);
        api.get("/facilities", { params: { fideicomiso_id: selected.id } })
            .then(({ data }) => setFacilities(data))
            .finally(() => setLoading(false));
    }, [selected?.id]);

    if (!selected) {
        return <div className="p-10 text-muted-foreground text-sm">Selecciona un fideicomiso en el header.</div>;
    }
    if (!cfg) {
        return <div className="p-10 text-muted-foreground text-sm">Sección no encontrada.</div>;
    }
    const Icon = cfg.Icon;

    return (
        <div className="p-8 lg:p-10 max-w-[1400px] space-y-6" data-testid={`hub-${hub}-page`}>
            <div>
                <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2">
                    V1 · Garantía · {selected.numero_interno}
                </div>
                <h1 className="font-display text-4xl tracking-tight flex items-center gap-3">
                    <Icon className="h-8 w-8 text-[color:var(--brand-dark-gold)]" />
                    {cfg.title}
                </h1>
                <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{cfg.desc}</p>
            </div>

            <div className="rounded-md border border-border bg-card overflow-hidden">
                <div className="px-4 py-2 border-b border-border text-[10px] uppercase tracking-widest text-muted-foreground">
                    Selecciona una facility para abrir su módulo {cfg.title}
                </div>
                {loading ? (
                    <div className="p-8 text-center text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin inline" />
                    </div>
                ) : facilities.length === 0 ? (
                    <div className="p-10 text-center text-sm text-muted-foreground">
                        No hay facilities en este fideicomiso.{" "}
                        <Link to="/v1/facilities" className="underline">Crear una</Link>.
                    </div>
                ) : (
                    <ul>
                        {facilities.map((f) => (
                            <li key={f.id} className="flex items-center gap-4 px-4 py-3 border-b border-border/50 hover:bg-muted/40" data-testid={`hub-facility-${f.numero}`}>
                                <div className="flex-1">
                                    <div className="font-medium">{f.numero} · {f.nombre_comercial}</div>
                                    <div className="text-[11px] text-muted-foreground">
                                        {f.tipo} · {f.moneda} · <span className="badge-gold">{f.estado}</span>
                                    </div>
                                </div>
                                <Link to={`/v1/facilities/${f.id}?tab=${cfg.tab}`}>
                                    <Button size="sm" variant="outline" data-testid={`hub-open-${f.numero}`}>
                                        Abrir {cfg.title} <ArrowUpRight className="h-3 w-3 ml-1" />
                                    </Button>
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
